import { useCallback, useState } from 'react';

import { redactGraphqlRequestErrorForTelemetry } from 'components/api/graphqlRequestError';
import { requestZeusFromBrowser } from 'components/api/zeusBrowserClient';
import { captureError } from 'helpers/captureError';

import { CustomerPrimaryPhoneNumberUpdateDocument } from './__generated__/tours.zeus';

export type SavePhoneNumberResult = { kind: 'saved' } | { kind: 'failed'; code: string | null };

async function savePrimaryPhoneNumber(phoneNumber: string): Promise<SavePhoneNumberResult> {
  let data;
  try {
    data = await requestZeusFromBrowser(CustomerPrimaryPhoneNumberUpdateDocument, {
      input: { phoneNumber },
    });
  } catch (error) {
    captureError(redactGraphqlRequestErrorForTelemetry(error), 'CustomerPrimaryPhoneNumberUpdate');
    return { kind: 'failed', code: null };
  }
  const result = data.customerPrimaryPhoneNumberUpdate;
  switch (result.__typename) {
    case 'Customer':
      return { kind: 'saved' };
    case 'InvalidInputError':
    case 'NotFoundError':
    case 'UnauthorizedError':
      return { kind: 'failed', code: result.code };
    default:
      return { kind: 'failed', code: null };
  }
}

export function useSavePhoneNumber() {
  const [isSaving, setIsSaving] = useState(false);

  const save = useCallback(async (phoneNumber: string): Promise<SavePhoneNumberResult> => {
    setIsSaving(true);
    try {
      return await savePrimaryPhoneNumber(phoneNumber);
    } finally {
      setIsSaving(false);
    }
  }, []);

  return { save, isSaving };
}
